import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fmtDate } from "../lib/api";
import { useEffect, useRef } from "react";
import { auth } from "../lib/auth";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";
import TermsBlock from "../components/TermsBlock";
import CategoryFilter from "../components/CategoryFilter";
import {
  Award, CheckCircle2, CreditCard, Flame, Gift, Mail, Plus, RefreshCcw, ShoppingBag,
  Sparkles,
} from "lucide-react";

function pct(n, d) { return d ? Math.min(100, (n / d) * 100) : 0; }

const PLAN_COLOR = {
  free: "border-zinc-700 text-zinc-300",
  starter: "border-emerald-500/60 text-emerald-300",
  elite: "border-purple-500/60 text-purple-300",
  pro: "border-amber-500/60 text-amber-300",
  team_base: "border-pink-500/60 text-pink-300",
  elite_annual: "border-cyan-500/60 text-cyan-300",
  pro_annual: "border-indigo-500/60 text-indigo-300",
};

function PlanCard({ plan, currentId, onPick, busy }) {
  const isCurrent = plan.plan_id === currentId;
  return (
    <div
      data-testid={`plan-card-${plan.plan_id}`}
      className={
        "border bg-[#0E0E0E] p-4 flex flex-col " +
        (isCurrent ? "border-amber-400 shadow-[0_0_0_1px_rgba(245,158,11,0.4)]" : "border-[#27272A] hover:border-zinc-500")
      }
    >
      <div className="flex items-center justify-between">
        <span className={"text-[11px] font-mono uppercase tracking-[0.2em] border px-2 py-1 " + (PLAN_COLOR[plan.plan_id] || "border-zinc-700")}>
          {plan.name}
        </span>
        {isCurrent && (
          <span className="text-[10px] font-mono uppercase text-amber-300 inline-flex items-center gap-1">
            <CheckCircle2 size={11} /> ACTIVE
          </span>
        )}
      </div>
      <div className="mt-3 font-mono">
        <span className="text-3xl tabnum">${(plan.price_cents / 100).toFixed(2)}</span>
        <span className="text-zinc-500 text-[12px] ml-1">
          {plan.billing_cycle === "annual" ? "/ year" : "/ mo"}
        </span>
      </div>
      <div className="text-[12px] text-zinc-400 mt-1">{plan.tagline}</div>
      <ul className="mt-3 space-y-1 text-[11px] font-mono">
        <li className={plan.features.includes("topics") ? "text-emerald-400" : "text-zinc-600"}>
          {plan.features.includes("topics") ? "✓" : "—"} 10 topic libraries
        </li>
        <li className={plan.features.includes("save") ? "text-emerald-400" : "text-zinc-600"}>
          {plan.features.includes("save") ? "✓" : "—"} Personal save library
        </li>
        <li className={plan.features.includes("innovation") ? "text-purple-300" : "text-zinc-600"}>
          {plan.features.includes("innovation") ? "✓" : "—"} Innovation Scout
        </li>
        <li className={plan.features.includes("bot") ? "text-amber-300" : "text-zinc-600"}>
          {plan.features.includes("bot")
            ? plan.billing_cycle === "annual"
              ? `✓ Bot · ${plan.monthly_bot_quota} every 30 days`
              : `✓ Bot · ${plan.monthly_bot_quota}/mo`
            : "— Bot"}
        </li>
        <li className={
          plan.billing_cycle === "annual"
            ? "text-cyan-300"
            : plan.features.includes("streak_bonus")
            ? "text-rose-300"
            : "text-zinc-600"
        }>
          {plan.billing_cycle === "annual"
            ? "✓ Auto-refill for 12 months"
            : plan.features.includes("streak_bonus")
            ? "✓ +15 free after 3 renewals"
            : "— renewal bonus"}
        </li>
      </ul>
      <button
        data-testid={`subscribe-${plan.plan_id}`}
        onClick={() => onPick(plan.plan_id)}
        disabled={isCurrent || busy}
        className={
          "mt-4 h-9 font-mono text-[11px] uppercase tracking-wider border " +
          (isCurrent
            ? "border-[#27272A] text-zinc-500 cursor-not-allowed"
            : "border-white bg-white text-black hover:bg-amber-300 hover:border-amber-300")
        }
      >
        {isCurrent ? "CURRENT_PLAN" : "SWITCH_TO_" + plan.name.toUpperCase()}
      </button>
    </div>
  );
}

export default function AccountPage() {
  const { user, refreshAccount } = useAuth();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const need = params.get("need");

  const { data: plans = [] } = useQuery({
    queryKey: ["plans"],
    queryFn: () => auth.get("/billing/plans").then((r) => r.data),
  });
  const { data: usage = [] } = useQuery({
    queryKey: ["usage"],
    queryFn: () => auth.get("/billing/usage").then((r) => r.data),
    enabled: !!user,
  });
  const { data: savesStats } = useQuery({
    queryKey: ["saves-stats"],
    queryFn: () => auth.get("/saves/stats").then((r) => r.data),
    enabled: !!user,
  });
  const { data: retention, refetch: refetchRetention } = useQuery({
    queryKey: ["retention-offers"],
    queryFn: () => auth.get("/billing/retention/offers").then((r) => r.data),
    enabled: !!user,
  });

  const subscribe = useMutation({
    mutationFn: (plan_id) =>
      auth.post(`/billing/subscribe?plan_id=${plan_id}`).then((r) => r.data),
    onSuccess: async () => {
      await refreshAccount();
      qc.invalidateQueries({ queryKey: ["usage"] });
      toast.success("PLAN_UPDATED");
    },
    onError: () => toast.error("PLAN_CHANGE_FAILED"),
  });
  const topup = useMutation({
    mutationFn: () => auth.post("/billing/topup").then((r) => r.data),
    onSuccess: async (data) => {
      await refreshAccount();
      const last = data?.last_topup;
      if (last?.bonus_qty) {
        toast.success(`BUY_5_GET_BONUS · +${last.paid_qty} paid · +${last.bonus_qty} BONUS`);
      } else {
        toast.success(`TOPUP +${last?.paid_qty || 10} USES · ${last?.next_free_in ?? 5} TO BONUS`);
      }
    },
  });
  const renew = useMutation({
    mutationFn: () => auth.post("/billing/renew").then((r) => r.data),
    onSuccess: async (data) => {
      await refreshAccount();
      await refetchRetention();
      if (data?.honour_year_bonus_awarded) {
        toast.success(`HONOUR_THE_YEAR · +${data.honour_year_bonus_awarded} FREE USES`);
      } else if (data?.first_renewal_bonus_awarded) {
        toast.success(`FIRST_RENEWAL_BONUS · +${data.first_renewal_bonus_awarded} FREE USES`);
      } else if (data?.retention_offer_emitted) {
        toast.success(
          `UPGRADE OFFER: Pro Annual @ $${(data.retention_offer_emitted.price_cents / 100).toFixed(2)}`,
          { description: "MOCKED email · view + accept below.", duration: 6000 },
        );
      } else if (data?.third_renewal_offer_token) {
        toast.success(
          "3RD_RENEWAL_DOUBLE_BONUS OFFER SENT",
          { description: "Check your email — accept to double this month's MindStein usages (refunds pause for the cycle).", duration: 6000 },
        );
      } else if (data?.bonus_awarded) {
        toast.success(`STREAK_BONUS · +${data.bonus_awarded} FREE USES`);
      } else {
        toast.success("RENEWED · streak +1");
      }
    },
  });
  const acceptThirdRenewal = useMutation({
    mutationFn: (token) =>
      auth.get(`/billing/offer/third-renewal/accept?token=${encodeURIComponent(token)}`)
        .then((r) => r.data),
    onSuccess: async (d) => {
      await refreshAccount();
      toast.success(d?.message || "3RD_RENEWAL_BONUS_ACCEPTED");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "OFFER_ACCEPT_FAILED"),
  });
  const acceptRetention = useMutation({
    mutationFn: (offer_id) =>
      auth.post("/billing/retention/accept", { offer_id }).then((r) => r.data),
    onSuccess: async (d) => {
      await refreshAccount();
      await refetchRetention();
      const cents = d?.accepted_retention_offer?.charged_cents || 0;
      toast.success(
        `UPGRADED_TO_PRO_ANNUAL · charged $${(cents / 100).toFixed(2)} (MOCKED)`,
      );
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "RETENTION_ACCEPT_FAILED"),
  });

  // Deep-link auto-accept for the emailed 3rd-renewal token: /account?offer=<t>
  const offerParam = params.get("offer");
  const autoAccepted = useRef(false);
  useEffect(() => {
    if (!offerParam || autoAccepted.current || !user) return;
    autoAccepted.current = true;
    acceptThirdRenewal.mutate(offerParam);
  }, [offerParam, user, acceptThirdRenewal]);

  if (!user) return null;
  const acc = user.account || {};
  const ent = acc.entitlements || {};
  const plan = acc.plan || { plan_id: "free", name: "Free", monthly_bot_quota: 0 };
  const remaining = acc.total_remaining ?? 0;
  const monthlyRem = acc.monthly_remaining ?? 0;
  const streak = acc.streak || {};

  return (
    <div data-testid="account-page" className="p-4 space-y-5 max-w-6xl mx-auto">
      {/* Personalized interest categories */}
      <div className="border border-[#27272A] bg-[#0A0A0A] p-3">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2">
          PERSONALIZED_CATEGORIES · SYNTHESIS_LENS
        </div>
        <CategoryFilter active={{ off: [] }} onChange={() => {}} />
      </div>
      {need && (
        <div className="border border-amber-500/40 bg-amber-500/10 p-3 flex items-center gap-3" data-testid="upgrade-needed-banner">
          <Sparkles size={14} className="text-amber-300" />
          <div className="text-[12px] text-amber-100 font-mono">
            You need a plan with <span className="font-bold uppercase">{need}</span> to access that page. Pick a plan below.
          </div>
        </div>
      )}

      {/* ── OFFER BANNERS (3rd-renewal + retention upgrade) ── */}
      {(() => {
        const sub = acc.subscription || {};
        const streak3rd = (acc.streak || {}).third_renewal_offer_token;
        const streak3rdStatus = (acc.streak || {}).third_renewal_offer_status;
        const showThird = streak3rd && streak3rdStatus !== "accepted";
        const latestRetention = retention?.latest;
        const alreadyOnAnnual = plan.plan_id === "pro_annual" || plan.plan_id === "elite_annual";
        const showRetention = latestRetention && latestRetention.status !== "accepted" && !alreadyOnAnnual;
        if (!showThird && !showRetention) return null;
        return (
          <section className="space-y-3">
            {showThird && (
              <div
                data-testid="third-renewal-offer-banner"
                className="border border-amber-500/60 bg-gradient-to-br from-amber-500/[0.08] to-transparent p-4 flex flex-wrap items-center gap-4"
              >
                <div className="flex items-center gap-2 text-amber-300">
                  <Gift size={16} />
                  <span className="text-[10px] font-mono uppercase tracking-[0.2em]">
                    3RD_RENEWAL_DOUBLE_BONUS_OFFER
                  </span>
                </div>
                <div className="flex-1 min-w-[220px] text-[12.5px] text-zinc-200 leading-relaxed">
                  You&apos;ve renewed 3 months in a row. Accept to{" "}
                  <span className="text-amber-300 font-mono">double this cycle&apos;s MindStein usages</span>{" "}
                  (+{plan.monthly_bot_quota || 0} bonus). Note: refunds pause for the current cycle
                  per the accepted offer terms.
                </div>
                <button
                  data-testid="third-renewal-accept-btn"
                  onClick={() => acceptThirdRenewal.mutate(streak3rd)}
                  disabled={acceptThirdRenewal.isPending}
                  className="h-10 px-4 bg-amber-400 text-black hover:bg-amber-300 border border-amber-400 font-mono uppercase text-[11px] tracking-wider font-extrabold disabled:opacity-50 inline-flex items-center gap-2"
                >
                  <CheckCircle2 size={12} />
                  {acceptThirdRenewal.isPending ? "ACCEPTING…" : "ACCEPT_DOUBLE_BONUS"}
                </button>
              </div>
            )}
            {showRetention && (
              <div
                data-testid="retention-offer-banner"
                className="border border-indigo-500/60 bg-gradient-to-br from-indigo-500/[0.08] to-transparent p-4 flex flex-wrap items-center gap-4"
              >
                <div className="flex items-center gap-2 text-indigo-300">
                  <Mail size={16} />
                  <span className="text-[10px] font-mono uppercase tracking-[0.2em]">
                    UPGRADE_OFFER · MOCKED_EMAIL
                  </span>
                </div>
                <div className="flex-1 min-w-[220px] text-[12.5px] text-zinc-200 leading-relaxed">
                  Upgrade to <span className="text-indigo-300 font-mono">PRO_ANNUAL</span> for just{" "}
                  <span className="text-emerald-300 font-mono">
                    ${(latestRetention.offer_price_cents / 100).toFixed(2)}
                  </span>
                  {latestRetention.permanent && (
                    <span className="text-emerald-300"> (final · permanent price)</span>
                  )}
                  {" "}— pay the difference, keep the 50 uses/30d refill + 100 first-renewal bonus.
                </div>
                <button
                  data-testid="retention-accept-btn"
                  onClick={() => acceptRetention.mutate(latestRetention.id)}
                  disabled={acceptRetention.isPending}
                  className="h-10 px-4 bg-indigo-400 text-black hover:bg-indigo-300 border border-indigo-400 font-mono uppercase text-[11px] tracking-wider font-extrabold disabled:opacity-50 inline-flex items-center gap-2"
                >
                  <CheckCircle2 size={12} />
                  {acceptRetention.isPending ? "UPGRADING…" : `UPGRADE_$${(latestRetention.offer_price_cents / 100).toFixed(0)}`}
                </button>
              </div>
            )}
          </section>
        );
      })()}

      <header className="flex flex-wrap items-end gap-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">ACCOUNT</div>
          <h1 className="font-mono text-3xl tracking-tight mt-1">{user.name || user.email}</h1>
          <p className="text-[12px] text-zinc-500 font-mono">{user.email}</p>
        </div>
        <div className="ml-auto flex items-end gap-5">
          <Stat label="PLAN" value={plan.name} sub={`${plan.monthly_bot_quota}${plan.billing_cycle === "annual" ? " every 30d" : "/mo"} bot quota`} color="text-amber-300" />
          <Stat label="REMAINING_USES" value={remaining} sub={`${monthlyRem} monthly · ${ent.bonus_balance || 0} bonus · ${ent.topup_balance || 0} top-up`} color="text-emerald-300" testId="remaining-uses" />
          <Stat label="RENEWAL_STREAK" value={streak.consecutive_months || 0} sub="3 → +15 bonus" color="text-rose-300" />
          <Stat label="SAVES" value={savesStats?.total ?? 0} sub="your library" color="text-purple-300" />
        </div>
      </header>

      {/* Plans */}
      <section>
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-2">CHOOSE_A_PLAN</div>
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-3">
          {plans.map((p) => (
            <PlanCard key={p.plan_id} plan={p} currentId={plan.plan_id}
              onPick={(id) => subscribe.mutate(id)} busy={subscribe.isPending} />
          ))}
        </div>
      </section>

      {/* Top-up + Renew + Quota bar */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="border border-[#27272A] bg-[#0E0E0E] p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300 font-mono inline-flex items-center gap-2">
            <ShoppingBag size={12} /> TOP_UP
          </div>
          <div className="text-[12px] text-zinc-400 mt-2">
            $9.99 = 10 MindStein usages. <span className="text-amber-300">Buy 5, get +10 bonus uses</span>.
            {" "}You&apos;re {acc.next_topup_free_in ?? 5} purchases from the next bonus.
          </div>
          <button
            data-testid="topup-button"
            onClick={() => topup.mutate()}
            disabled={topup.isPending}
            className="mt-3 h-9 px-3 border border-amber-400 bg-amber-400 text-black hover:bg-amber-300 font-mono uppercase text-[11px] tracking-wider disabled:opacity-50 inline-flex items-center gap-2"
          >
            <Plus size={11} />
            {topup.isPending ? "BUYING…" : "BUY_TOPUP · $9.99"}
          </button>
          <div className="mt-3 text-[11px] font-mono text-zinc-500">
            Top-up balance: <span className="text-white tabnum">{ent.topup_balance || 0}</span> ·
            Purchases: <span className="tabnum">{acc.topups_purchased ?? 0}</span>
          </div>
        </div>

        <div className="border border-[#27272A] bg-[#0E0E0E] p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-rose-300 font-mono inline-flex items-center gap-2">
            <Flame size={12} /> RENEWAL_STREAK
          </div>
          <div className="text-[12px] text-zinc-400 mt-2">
            Renew 3 months in a row on <span className="text-purple-300">Elite</span> or <span className="text-amber-300">Pro</span> for the{" "}
            <span className="text-amber-300">3RD_RENEWAL_DOUBLE_BONUS</span> · monthly plans only.
          </div>
          <div className="mt-3 h-[6px] bg-[#1a1a1a]">
            <div className="bg-rose-400" style={{ width: `${pct(streak.consecutive_months || 0, 3)}%`, height: "100%" }} />
          </div>
          <div className="text-[10px] text-zinc-500 mt-1 font-mono">
            {streak.consecutive_months || 0} / 3 consecutive months
          </div>
          <button
            data-testid="renew-button"
            onClick={() => renew.mutate()}
            disabled={renew.isPending}
            className="mt-3 h-9 px-3 border border-rose-400 text-rose-200 hover:bg-rose-400 hover:text-black font-mono uppercase text-[11px] tracking-wider disabled:opacity-50 inline-flex items-center gap-2"
          >
            <RefreshCcw size={11} />
            {renew.isPending ? "RENEWING…" : "MOCK_RENEW"}
          </button>
        </div>

        <div className="border border-[#27272A] bg-[#0E0E0E] p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-purple-300 font-mono inline-flex items-center gap-2">
            <Award size={12} /> QUOTA
          </div>
          <div className="mt-3 text-[12px] font-mono">
            <Row label="MONTHLY" value={`${ent.monthly_used || 0} / ${plan.monthly_bot_quota || 0}`}
              bar={pct(ent.monthly_used || 0, plan.monthly_bot_quota || 1)} color="bg-amber-400" />
            <Row label="BONUS" value={ent.bonus_balance || 0} bar={pct(ent.bonus_balance || 0, 30)} color="bg-rose-400" />
            <Row label="TOPUP" value={ent.topup_balance || 0} bar={pct(ent.topup_balance || 0, 50)} color="bg-purple-400" />
            <div className="mt-3 border-t border-[#27272A] pt-2 flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">TOTAL_REMAINING</span>
              <span className="text-2xl text-emerald-300 tabnum">{remaining}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Usage log */}
      <section className="border border-[#27272A]">
        <div className="px-3 py-2 border-b border-[#27272A] text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2">
          <CreditCard size={11} /> USAGE_LEDGER · last {usage.length}
        </div>
        <table className="w-full text-[11px] font-mono">
          <thead className="text-zinc-500">
            <tr className="border-b border-[#161616]">
              <th className="text-left p-2 uppercase">WHEN</th>
              <th className="text-left p-2 uppercase">EVENT</th>
              <th className="text-left p-2 uppercase">SOURCE</th>
            </tr>
          </thead>
          <tbody>
            {usage.map((u) => (
              <tr key={u.id} className="border-b border-[#0E0E0E]">
                <td className="p-2 text-zinc-400">{fmtDate(u.used_at)}</td>
                <td className="p-2 uppercase">{u.event}</td>
                <td className="p-2 uppercase text-amber-300">{u.source}</td>
              </tr>
            ))}
            {usage.length === 0 && (
              <tr><td colSpan={3} className="p-4 text-center text-zinc-500">NO_USAGE_YET · send a bot message to begin</td></tr>
            )}
          </tbody>
        </table>
      </section>

      {/* Terms + disclaimer — above the link to MY_LIBRARY per Phase 11 */}
      <TermsBlock id="account" />

      <div className="text-[11px] text-zinc-500 font-mono">
        Need help? Email{" "}
        <a className="text-amber-300 hover:underline" href="mailto:hello@mindstein.app">hello@mindstein.app</a>
        .
        {" "}
        <Link to="/library" className="text-purple-300 hover:underline">VIEW_MY_LIBRARY →</Link>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, color, testId }) {
  return (
    <div data-testid={testId} className="border border-[#27272A] bg-[#0E0E0E] p-2 min-w-[140px]">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">{label}</div>
      <div className={"text-2xl font-mono tabnum mt-1 " + (color || "text-white")}>{value}</div>
      <div className="text-[10px] text-zinc-500 font-mono mt-1">{sub}</div>
    </div>
  );
}

function Row({ label, value, bar, color }) {
  return (
    <div className="mb-2">
      <div className="flex items-center justify-between text-[11px]">
        <span className="text-zinc-500 uppercase tracking-wider">{label}</span>
        <span className="tabnum">{value}</span>
      </div>
      <div className="mt-1 h-[3px] bg-[#1a1a1a]">
        <div className={color} style={{ width: `${bar}%`, height: "100%" }} />
      </div>
    </div>
  );
}
